/* lib/splashNativo.js -- QUITAR LA PANTALLA NATIVA DE ARRANQUE. UNA sola copia.
 *
 * #457 (P14). El problema que resuelve, medido en el codigo:
 *
 *   iOS ensena `LaunchScreen.storyboard` mientras la app arranca y lo QUITA en
 *   cuanto la ventana aparece -- que es MUCHO antes de que el WKWebView haya
 *   cargado el bundle y React haya montado `BootSplash`. En ese hueco lo unico
 *   que se ve es el fondo del WKWebView: `#111111` liso, sin logo. Eso es la
 *   "pantalla negra al abrir la app" de P14. Despues aparece el logo del
 *   `BootSplash`, y la costura se nota.
 *
 *   `@capacitor/splash-screen` cierra el hueco: al cargar el puente instancia el
 *   view controller inicial de `LaunchScreen.storyboard` y lo pone ENCIMA del
 *   webview (`SplashScreen.swift:91-93`, leido en `node_modules`). Con
 *   `launchAutoHide: false` se queda ahi hasta que alguien pide quitarlo. Quien
 *   lo pide es esto, y lo pide cuando el logo de la web YA ESTA PINTADO DEBAJO,
 *   asi que la transicion es invisible: el mismo logo, del mismo tamano, en el
 *   mismo sitio, sobre el mismo fondo.
 *
 * LOS TRES CINTURONES, copiados de `lib/haptics.js` sin inventar nada:
 *
 *   1. GATE app-only y shell nativo real, evaluado POR LLAMADA
 *      (`IS_APP_BUILD` + `Capacitor.isNativePlatform()`). El plugin trae
 *      implementacion web, y en web no hay ninguna pantalla nativa que quitar.
 *
 *   2. IMPORT DINAMICO Y CACHEADO, y se guarda la PROMESA del modulo, no el
 *      plugin pelado: `SplashScreen` es un proxy de `registerPlugin` y devolverlo
 *      como valor de resolucion colgaria la promesa para siempre (el hang de boot
 *      de F4.2c). Webpack lo parte en un chunk que el bundle web jamas pide.
 *
 *   3. FIRE-AND-FORGET Y A PRUEBA DE TODO: nadie espera a que se quite. Si el
 *      plugin falta, truena o el puente no contesta, la app sigue viva -- con la
 *      pantalla de arranque encima como mucho hasta que el vigilante de
 *      `index.js` vuelve a pedirlo. Jamas un error al usuario por esto.
 *
 * IDEMPOTENTE POR CONSTRUCCION: `hide()` sobre un splash ya quitado es un no-op
 * en el plugin (`hideSplash` sale por lo bajo si no esta visible), asi que los
 * DOS sitios que lo llaman -- `BootSplash.js` al primer pintado y el vigilante de
 * `index.js` a los 4 s -- pueden dispararse los dos sin que se note.
 *
 * SIN FUNDIDO (`fadeOutDuration: 0`) a proposito: debajo ya esta pintado el MISMO
 * logo. Un fundido cruzado entre dos imagenes identicas solo se veria como un
 * parpadeo de brillo.
 */
import { IS_APP_BUILD } from './buildTarget';

/** Los 4 s del vigilante. Exportada para que la prueba mida el numero, no el pintado. */
export const VIGILANTE_MS = 4000;

let splashModulePromise = null;

/** True solo en el build de app corriendo dentro del shell nativo de verdad. */
export function splashNativoActivo() {
  return IS_APP_BUILD
    && typeof window !== 'undefined'
    && !!window.Capacitor?.isNativePlatform?.();
}

/** Quita la pantalla nativa de arranque, sin fundido. Fire-and-forget. No-op en web. */
export function ocultarSplashNativo() {
  if (!splashNativoActivo()) return;
  try {
    if (!splashModulePromise) splashModulePromise = import('@capacitor/splash-screen');
    splashModulePromise
      .then((mod) => mod.SplashScreen.hide({ fadeOutDuration: 0 }))
      .catch(() => { /* la app ya esta viva debajo; el vigilante lo reintenta */ });
  } catch (e) {
    // Un plugin ausente no puede costar un arranque.
  }
}

/** El VIGILANTE: nadie puede quedarse debajo de la pantalla de arranque.
 *
 * Si `BootSplash` no llegara a montarse -- una excepcion temprana, una ruta que no
 * lo cubre, lo que sea --, nadie pediria quitar el splash y la app quedaria detras
 * de una imagen fija para siempre. Esto lo impide: a los 4 s se pide igual. Los
 * dos `hide` son idempotentes.
 *
 * Devuelve el `clearTimeout` para que una prueba pueda limpiarlo. En web no
 * programa nada.
 */
export function armarVigilanteDelSplash() {
  if (!splashNativoActivo()) return () => {};
  const t = setTimeout(ocultarSplashNativo, VIGILANTE_MS);
  return () => clearTimeout(t);
}

/** Solo para tests: devuelve el cacheo del modulo a su estado de arranque. */
export function __resetSplashParaTests() {
  splashModulePromise = null;
}
